import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';

const SUSPENDED_BANNER = (
  <div style={{
    background: '#fbbf24',
    color: '#78350f',
    padding: '12px',
    textAlign: 'center',
    fontWeight: 600,
    zIndex: 1000,
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
  }}>
    This organization's account is currently suspended. Some features may be unavailable.
  </div>
);

export const SuspensionGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log("SuspensionGuard: render start");
  const { user, userRole, loading } = useAuth();
  const location = useLocation();

  // Check if signup is in progress - if so, skip organization loading to prevent interruptions
  const signupInProgress = sessionStorage.getItem('signup-in-progress') === 'true';
  
  // If signup is in progress, don't load organization data and don't check suspension
  if (signupInProgress) {
    console.log("SuspensionGuard: Signup in progress, skipping org checks");
    return <>{children}</>;
  }

  const { organization, loading: orgLoading } = useOrganization();

  // Wait for auth/org to load
  if (loading || orgLoading) return <>{children}</>;
  // If org is suspended
  if (organization && organization.is_suspended) {
    // Bizadmin/owner: redirect to suspended page (unless already there)
    if (userRole === 'bizadmin' || userRole === 'owner') {
      if (location.pathname !== '/account-suspended') {
        return <Navigate to="/account-suspended" replace state={{ from: location }} />;
      }
      // If already on the suspended page, just render children (the page)
      return <>{children}</>;
    }
    // Staff/parent: show banner but allow access
    return <>
      {SUSPENDED_BANNER}
      <div style={{ marginTop: 48 }}>{children}</div>
    </>;
  }

  // Not suspended: render as normal
  return <>{children}</>;
};

export default SuspensionGuard; 